import { useTranslation } from "react-i18next";
import type { MCQOption } from "../content-types";
import { Explanation, Markup, OptionRow, Prompt } from "./shared";
import { GlossableText, isLanguageFramework } from "./glossable-text";
import type { QuestionRendererProps } from "./types";

interface ConceptMapContent {
  concept_a?: string;
  concept_b?: string;
  relationship_prompt?: string;
  options: MCQOption[];
  correct_option_id: string;
  explanation: string;
}

/** concept_map — the two concepts shown side by side (left → right), then the
 *  relationship question + single-select options. (Plain MCQ rendering dropped
 *  the concepts, leaving "the concept on the left/right" with nothing shown.) */
export function ConceptMapRenderer({
  content,
  answer,
  setAnswer,
  revealed,
  frameworkSlug,
  translateAnswers,
}: QuestionRendererProps) {
  const { t } = useTranslation();
  const c = content as unknown as ConceptMapContent;
  const options: MCQOption[] = c.options ?? [];
  const selected = answer as string | undefined;
  const enableGloss = isLanguageFramework(frameworkSlug);
  const enableAnswerGloss = enableGloss && !!translateAnswers;
  const relPrompt = c.relationship_prompt || t("How are these two concepts related?");

  const stateFor = (id: string): "neutral" | "selected" | "correct" | "wrong" => {
    if (!revealed) return selected === id ? "selected" : "neutral";
    if (id === c.correct_option_id) return "correct";
    if (selected === id) return "wrong";
    return "neutral";
  };

  return (
    <div>
      <Prompt>{enableGloss ? <GlossableText text={relPrompt} context={relPrompt} /> : relPrompt}</Prompt>

      <div className="mt-4 grid grid-cols-[1fr_auto_1fr] items-stretch gap-2">
        <ConceptBox text={c.concept_a} gloss={enableGloss} context={relPrompt} />
        <div className="flex items-center justify-center text-xl text-ink-faint">→</div>
        <ConceptBox text={c.concept_b} gloss={enableGloss} context={relPrompt} />
      </div>

      <div className="mt-4 space-y-2">
        {options.map((o) => (
          <OptionRow
            key={o.id}
            state={stateFor(o.id)}
            disabled={revealed}
            onClick={() => !revealed && setAnswer(o.id)}
          >
            <span className="flex h-6 w-6 shrink-0 items-center justify-center rounded-md bg-black/5 text-xs font-bold text-ink-soft">
              {o.id}
            </span>
            <span className="text-ink">
              {enableAnswerGloss ? (
                <GlossableText text={o.text} context={relPrompt} />
              ) : (
                <Markup>{o.text}</Markup>
              )}
            </span>
          </OptionRow>
        ))}
      </div>

      {revealed && <Explanation text={c.explanation} />}
    </div>
  );
}

function ConceptBox({
  text,
  gloss,
  context,
}: {
  text?: string;
  gloss?: boolean;
  context?: string;
}) {
  return (
    <div className="flex items-center justify-center rounded-xl border border-brand-200 bg-brand-50 px-3 py-4 text-center text-sm font-semibold text-brand-800">
      {gloss && text ? <GlossableText text={text} context={context} /> : <Markup>{text || "—"}</Markup>}
    </div>
  );
}
